import React, { useState } from "react";
import { Button, PreLoading, SvgIcons } from "@tripian/react";
import moment from "moment";
import Model, { Providers } from "@tripian/model";
import { providers } from "@tripian/core";
import { useNavigate } from "react-router-dom";
import bookingConfirmedBg from "./img/booking_confirmed.jpg";
import { TRIP_PLANNER_VALAI } from "../../../../constants/ROUTER_PATH_TITLE";

interface BookingData {
  tourName: string;
  tourImage: string;
  travelDate: string;
  travelHour: string;
  duration: number; // in minutes
  coordinate?: { lat: number; lng: number };
}

interface IViatorBookingDetails {
  items?: Providers.Viator.BookingConfirmItem[];
  totalConfirmedPrice?: Providers.Viator.BookingConfirmTotalPrice;
  voucherInfo?: Providers.Viator.VoucherInfo;
  tourName: string;
  tourImage: string;
  travelDate?: string;
  travelHour?: string;
  loading?: boolean;
  buttonText?: string;
  showButton?: boolean;
  clicked: () => void;
  t: (value: Model.TranslationKey) => string;
  // Trip planner navigation data
  cityId?: number;
  startDate?: string;
  endDate?: string;
  adults?: number;
  children?: number;
  // Booking duration in minutes
  duration?: number;
  // Tour coordinate
  tourCoordinate?: { lat: number; lng: number };
  // Accommodation coordinate from URL (lat/lng params)
  accommodationLat?: number;
  accommodationLng?: number;
}

const ViatorBookingDetails: React.FC<IViatorBookingDetails> = ({
  items,
  totalConfirmedPrice,
  voucherInfo,
  tourName,
  tourImage,
  travelDate,
  travelHour,
  loading,
  buttonText,
  showButton = true,
  clicked,
  t,
  cityId,
  startDate,
  endDate,
  adults,
  children,
  duration,
  tourCoordinate,
  accommodationLat,
  accommodationLng,
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [loadingRefund, setLoadingRefund] = useState<boolean>(false);
  const [refundDetail, setRefundDetail] =
    useState<Providers.Viator.BookingRefund>();

  // Handle trip planner navigation with booking data
  const handleGetStarted = () => {
    // tripStartDate: travelDate + travelHour + duration (tour end time)
    // tripEndDate: same day at 21:00
    let tripStartDate: string | undefined;
    let tripEndDate: string | undefined;

    if (travelDate && duration) {
      // Use travelHour if provided, otherwise default to 09:00
      const effectiveHour = travelHour || "09:00";
      // Combine travelDate with travelHour, then add duration
      const tourEndTime = moment(travelDate)
        .set({
          hour: parseInt(effectiveHour.split(':')[0]),
          minute: parseInt(effectiveHour.split(':')[1])
        })
        .add(duration, 'minutes');
      tripStartDate = tourEndTime.format("YYYY-MM-DDTHH:mm");
      tripEndDate = moment(travelDate).set({ hour: 21, minute: 0 }).format("YYYY-MM-DDTHH:mm");
    } else if (travelDate) {
      // Fallback if no duration
      tripStartDate = moment(travelDate).format("YYYY-MM-DDTHH:mm");
      tripEndDate = moment(travelDate).set({ hour: 21, minute: 0 }).format("YYYY-MM-DDTHH:mm");
    }

    // Create booking data object to pass via URL
    // Note: travelHour can be empty string for tours without specific start times
    const bookingData: BookingData | undefined = (travelDate && duration) ? {
      tourName,
      tourImage,
      travelDate,
      travelHour: travelHour || "09:00", // Default to 9 AM if no specific time
      duration,
      coordinate: tourCoordinate,
    } : undefined;

    if (cityId && tripStartDate && tripEndDate) {
      const params = new URLSearchParams({
        cityId: cityId.toString(),
        arrivalDate: tripStartDate,
        departureDate: tripEndDate,
        adults: bookedAdults.toString(),
        children: bookedChildren.toString(),
      });

      // Add booking data as encoded JSON if available
      if (bookingData) {
        params.set('booking', encodeURIComponent(JSON.stringify(bookingData)));
      }

      // Add accommodation coordinates if available from URL params
      if (accommodationLat !== undefined && accommodationLng !== undefined) {
        params.set('lat', accommodationLat.toString());
        params.set('lng', accommodationLng.toString());
      }

      const url = `${TRIP_PLANNER_VALAI.PATH}?${params.toString()}`;
      navigate(url);
    } else {
      console.warn('Missing required data for trip planner:', { cityId, tripStartDate, tripEndDate });
      // Fallback: Navigate to trip planner without params
      navigate(TRIP_PLANNER_VALAI.PATH);
    }
  };

  // Extract traveler details from booking
  const travelerDetails = items?.[0]?.lineItems
    ? items[0].lineItems
        .map(item => {
          return `${item.numberOfTravelers} ${item.ageBand}`;
        })
        .join(", ")
    : "N/A";

  // Extract adults and children counts from booking items, fallback to props
  const bookedAdults = items?.[0]?.lineItems
    ?.filter(item => item.ageBand === "ADULT")
    .reduce((sum, item) => sum + item.numberOfTravelers, 0) || adults || 1;

  const bookedChildren = items?.[0]?.lineItems
    ?.filter(item => item.ageBand === "CHILD")
    .reduce((sum, item) => sum + item.numberOfTravelers, 0) || children || 0;

  const totalPrice = totalConfirmedPrice?.price?.recommendedRetailPrice ?? 0;

  const travelerPricingDetails = items?.[0]?.lineItems
    ? items[0].lineItems
        .map(item => {
          const subtotal =
            item.subtotalPrice?.price?.recommendedRetailPrice ?? 0;
          return `${item.numberOfTravelers} ${item.ageBand} $${subtotal}`;
        })
        .join(", ")
    : "N/A"; // Provide a fallback value if data is not available

  const bookingStatus = items?.[0]?.status;

  // ValaiLanding Design
  if (window.tconfig.LANDING_PAGE === "ValaiLanding") {
    return (
      <div className="w-full bg-white rounded-2xl overflow-hidden" style={{ margin: "0 auto" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <SvgIcons.Check2 fill="#fff" width="20" height="20" />
            </div>
            <h2 className="text-xl font-bold">Booking confirmed!</h2>
          </div>

          <button
            onClick={clicked}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex-shrink-0"
          >
            <SvgIcons.X width="20" height="20" />
          </button>
        </div>

        {/* Tour Details Container */}
        <div className="px-6 pb-6">
          <div className="flex gap-4 items-start">
            {/* Tour Image */}
            <img
              src={tourImage}
              alt={tourName}
              className="w-[145px] h-[110px] object-cover rounded-lg flex-shrink-0"
            />

            {/* Tour Info & Price Container */}
            <div className="flex-1 flex flex-col">
              {/* Tour Title */}
              <h3 className="font-semibold text-base mb-3 line-clamp-2">{tourName}</h3>

              {/* Tour Details Icons */}
              <div className="space-y-2 text-sm text-gray-700 mb-4">
                {/* Person Count */}
                <div className="flex items-center gap-2">
                  <SvgIcons.Avatar size="0.875rem" fill="#000" />
                  <span>{travelerDetails}</span>
                </div>

                {/* Date & Time */}
                <div className="flex items-center gap-2">
                  <SvgIcons.Calendar size="0.875rem" fill="#000" />
                  <span>
                    {moment(travelDate).format("ddd, MMM DD, YYYY")}
                    {travelHour && ` • ${moment(travelHour, "HH:mm").format("hh:mm A")}`}
                  </span>
                </div>

                {/* Free Cancellation */}
                <div className="flex items-center gap-2">
                  <SvgIcons.Check2 width="14" height="14" fill="#000" />
                  <span>Free Cancellation</span>
                </div>
              </div>

              {/* Total Price - Right aligned */}
              <div className="text-right mt-auto">
                <span className="text-base font-semibold">Total Price: ${totalPrice}</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div
          className="relative h-[200px] bg-cover bg-center flex justify-between content-end flex-wrap p-6 border-radius-12-c"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${bookingConfirmedBg})`
          }}
        >
          <div>
              <h3 className="text-white text-2xl font-bold mb-2">
                  Want to plan the rest of your trip?
              </h3>
              <p className="text-white text-base mb-4">
                  Add this tour to your trip plan in seconds.
              </p>
          </div>
          <button
            onClick={handleGetStarted}
            className="bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-3 border-radius-56-c self-end transition-colors"
          >
            GET STARTED
          </button>
        </div>
      </div>
    );
  }

  // Original Design
  return (
    <div className="p-4 w-full">
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          {loadingRefund ? (
            <div>
              <PreLoading size="small" color="#fff" />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-6 w-96">
              <h2 className="text-xl font-bold mb-4 text-center">
                {refundDetail?.status}
              </h2>
              <div className="product-info mt-4 flex flex-col text-sm">
                <ul className="list-none p-0 m-0 flex flex-col gap-2 text-sm font-medium mb-2">
                  <li className="flex items-start">
                    {t(
                      "trips.myTrips.localExperiences.tourDetails.cancellation.refundAmount"
                    )}
                    :
                    <span className="ml-2">
                      {refundDetail?.refundDetails.refundAmount}{" "}
                      {refundDetail?.refundDetails.currencyCode}
                    </span>
                  </li>
                  <li className="flex items-start">
                    {t(
                      "trips.myTrips.localExperiences.tourDetails.cancellation.refundPercentage"
                    )}
                    :
                    <span className="ml-2">
                      %{refundDetail?.refundDetails.refundPercentage}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <Button text="Cancel" onClick={() => setModalOpen(false)} />
                <Button
                  color="primary"
                  text="Continue"
                  onClick={() => {
                    setModalOpen(false);
                    clicked();
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <div>
        <div className="row center mb0">
          <div
            className={`w-20 h-20 flex items-center justify-center rounded-full ${
              bookingStatus === "REJECTED"
                ? "bg-red-600"
                : bookingStatus === "PENDING"
                  ? "bg-yellow-500"
                  : "bg-green-600"
            }`}
          >
            {bookingStatus === "REJECTED" ? (
              <SvgIcons.Exclamation2 fill="#fff" size="4rem" />
            ) : bookingStatus === "PENDING" ? (
              <SvgIcons.Time fill="#fff" size="4rem" />
            ) : (
              <SvgIcons.Success fill="#fff" size="4rem" />
            )}
          </div>
        </div>
        <div className="row center mb0 mt4">
          <div className="col col12 mb0">
            <h4 className="text-lg font-bold line-clamp-3">
              {bookingStatus === "REJECTED"
                ? t(
                    "trips.myTrips.localExperiences.tourDetails.bookingStatus.rejected.title"
                  )
                : bookingStatus === "PENDING"
                  ? t(
                      "trips.myTrips.localExperiences.tourDetails.bookingStatus.pending.title"
                    )
                  : t(
                      "trips.myTrips.localExperiences.tourDetails.bookingStatus.confirmed.title"
                    )}
            </h4>
          </div>
          {/* {bookingStatus === "CONFIRMED" && !voucherInfo?.isVoucherRestrictionRequired && (
            <div className="w-full mt-4 mb-4 font-medium underline text-base line-clamp-2">
              <a className="" rel="noopener noreferrer" target="_blank" href={voucherInfo?.url}>
                {t("trips.myTrips.localExperiences.tourDetails.goToTicketDetails")}
              </a>
            </div>
          )} */}
        </div>
        <div className="center mb0">
          <div className="relative m-0 p-0">
            <div className="rounded-lg bg-white">
              <div className="p-5 text-black">
                {bookingStatus === "REJECTED" ? (
                  <div className="text-base font-medium">
                    {t(
                      "trips.myTrips.localExperiences.tourDetails.bookingStatus.rejected.description"
                    )}
                  </div>
                ) : bookingStatus === "PENDING" ? (
                  <div className="text-base font-medium">
                    {t(
                      "trips.myTrips.localExperiences.tourDetails.bookingStatus.pending.description"
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="photo-row flex gap-4 items-start">
                      <img
                        className="w-20 h-20 rounded-md object-cover overflow-hidden shrink-0"
                        src={tourImage}
                        alt=""
                      />
                      <div>
                        <div className="Likely to Sell Out mb-1 flex items-start">
                          <div className="bg-green-600 text-white inline-flex items-center py-3 px-2 rounded-sm h-6 max-w-full">
                            <strong className="overflow-ellipsis self-auto whitespace-nowrap leading-5 text-sm font-bold">
                              {t(
                                "trips.myTrips.localExperiences.tourDetails.freeCancellation"
                              )}
                            </strong>
                          </div>
                        </div>
                        <span className="text-sm font-bold line-clamp-3 text-start">
                          {tourName}
                        </span>
                      </div>
                    </div>
                    <div className="product-info mt-4 flex flex-col text-sm">
                      <ul className="list-none p-0 m-0 flex flex-col gap-2 text-sm font-medium mb-2">
                        <li className="flex items-start">
                          <SvgIcons.Avatar
                            className="mt-[0.125rem] mr-2"
                            size="0.875rem"
                            fill="#000"
                          />
                          <span>{travelerDetails}</span>
                        </li>
                        <li className="flex items-start">
                          <SvgIcons.Calendar
                            className="mt-[0.175rem] mr-2"
                            size="0.875rem"
                            fill="#000"
                          />
                          <span>
                            {moment(travelDate).format("ddd, MMM DD, YYYY")}
                          </span>
                          {travelHour && (
                            <>
                              <span className="whitespace-pre-wrap px-1">
                                {" "}
                                •{" "}
                              </span>
                              <span>
                                {moment(travelHour, "HH:mm").format("hh:mm A")}
                              </span>
                            </>
                          )}
                        </li>
                      </ul>
                    </div>

                    <div className="pricing-row flex items-end gap-2 flex-row-reverse text-end">
                      <div className="font-bold">{travelerPricingDetails}</div>
                    </div>
                    <hr className="my-4" style={{ opacity: 0.6 }} />
                    <div className="flex justify-between font-bold w-full text-lg">
                      <div>
                        {t(
                          "trips.myTrips.localExperiences.tourDetails.totalPrice"
                        )}
                      </div>
                      <div>${totalPrice}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showButton && (
          <div className="row center">
            <div>
              {loading ? (
                <div>
                  <PreLoading size="small" bgColor="#fff" />
                </div>
              ) : (
                <Button
                  color="primary"
                  text={buttonText}
                  onClick={() => {
                    if (
                      buttonText ===
                      t(
                        "trips.myTrips.localExperiences.tourDetails.cancelReservation"
                      )
                    ) {
                      setLoadingRefund(true);
                      providers.viator
                        ?.bookingRefundDetails(items?.[0].bookingRef || "")
                        .then(data => {
                          setRefundDetail(data);
                        })
                        .finally(() => setLoadingRefund(false));
                      setModalOpen(true);
                    } else {
                      clicked();
                    }
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViatorBookingDetails;
